import React from "react";
import { Wrapper } from "../wrapper";
import { Form } from "@components";
import { Grid } from "@material-ui/core";

export type KiteworksFormValues = {
  firstname: string;
  lastname: string;
  organization: string;
  emailAddress: string;
  phoneNumber: string;
  multipleUserAdditions: string;

  globalPaymentsSponsor: string;
  globalPaymentsSponsorEmail: string;

  locationOfBusiness: string;
  tenant: string;

  accessStart: Date | null;
  accessEnd: Date | null;

  businessJustification: string;
};

type Key = keyof KiteworksFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: KiteworksFormValues) => void;
  open: boolean;
  values: Partial<KiteworksFormValues>;
  readonly?: boolean;
};

export const KiteworksForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<KiteworksFormValues>({
    firstname: values?.firstname || "",
    lastname: values?.lastname || "",
    organization: values?.organization || "",
    emailAddress: values?.emailAddress || "",
    phoneNumber: values?.phoneNumber || "",
    multipleUserAdditions: values?.multipleUserAdditions || "",

    globalPaymentsSponsor: values?.globalPaymentsSponsor || "",
    globalPaymentsSponsorEmail: values?.globalPaymentsSponsorEmail || "",

    locationOfBusiness: values?.locationOfBusiness || "",
    tenant: values?.tenant || "",

    accessStart: values?.accessStart || null,
    accessEnd: values?.accessEnd || null,

    businessJustification: values?.businessJustification || "",
  });

  // ---------- VALIDATION ----------
  function validateFormValue([name, value]: [Key, any]): Promise<
    [Key, Error | undefined]
  > {
    switch (name) {
      case "firstname":
      case "lastname":
      case "organization":
      case "emailAddress":
      case "phoneNumber":
      case "globalPaymentsSponsor":
      case "globalPaymentsSponsorEmail":
      case "locationOfBusiness":
      case "tenant":
        return Promise.resolve([name, Form.Validators.hasValue(value)]);

      default:
        return Promise.resolve([name, undefined]);
    }
  }

  function onFormValueChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validateFormValue([name, value]).then(([field, error]) => {
      form.set(Form.target.ERROR, [field, error]);
    });
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validateFormValue([name as Key, value])
      )
    ).then((validations: [Key, Error | undefined][]) => {
      const valid = validations.every(([_, error]) => !error);

      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([field, error]) => {
          form.set(Form.target.ERROR, [field, error]);
        });
      }
    });
  }

  // ---------- OPTIONS ----------
  const tenantOptions = [
    "International EMEA - Sensitive Non-PCI Data (LAN / Corp Site)",
    "International EMEA - Sensitive PCI Data (Secure Environment)",
    "North America - Sensitive Non-PCI Data (LAN / Corp Site)",
    "North America - Sensitive PCI Data (Secure Environment)",
  ];

  const locationOptions = [
    { label: "International", value: "International" },
    { label: "North America", value: "North America" },
  ];

  // ---------- UI ----------
  return (
    <Wrapper
      onSubmit={onFormSubmit}
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      open={open}
      title="External Recipient User Information"
      size="md"
      readonly={readonly}
    >
      <Form>
        {/* --------------------------------------- */}
        {/* External User Details */}
        {/* --------------------------------------- */}

        <Form.Textbox
          name="firstname"
          label="First Name"
          required
          size={6}
          error={form.errors.firstname}
          value={form.values.firstname}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="lastname"
          label="Last Name"
          required
          size={6}
          error={form.errors.lastname}
          value={form.values.lastname}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="organization"
          label="Organization"
          required
          size={6}
          error={form.errors.organization}
          value={form.values.organization}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="emailAddress"
          label="Email Address"
          required
          size={6}
          error={form.errors.emailAddress}
          value={form.values.emailAddress}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="phoneNumber"
          label="Phone Number"
          required
          size={6}
          error={form.errors.phoneNumber}
          value={form.values.phoneNumber}
          onChange={onFormValueChange}
        />

        {/* --------------------------------------- */}
        {/* NOTES BLOCK */}
        {/* --------------------------------------- */}
        <Form.Group>
          <Form.Title>NOTES</Form.Title>

          <Form.Text>
            1.{" "}
            <a
              href="https://docs.google.com/spreadsheets/d/1NArSIgghD1SJUcIGsoSvle-BLAH7eND7gCwj5bySxd0/edit?gid=0#gid=0"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Make a copy of this template.
            </a>
          </Form.Text>

          <Form.Text>2. Fill out the copy with the User Information.</Form.Text>

          <Form.Text>
            3. Share the file with {"<assignee group email address>"}.
          </Form.Text>

          <Form.Text>4. Copy the URL and paste it in the box below.</Form.Text>

          <Form.Text>
            5. Bulk users can only be added under the same Tenant. A new request
            is required for each different tenant.
          </Form.Text>
        </Form.Group>

        {/* Multiple User Additions */}
        <Form.Textarea
          name="multipleUserAdditions"
          label="Multiple User Additions"
          size={12}
          value={form.values.multipleUserAdditions}
          onChange={onFormValueChange}
        />

        {/* --------------------------------------- */}
        {/* Sponsor */}
        {/* --------------------------------------- */}
        <Form.Title>Sponsor Information</Form.Title>

        <Form.Textbox
          name="globalPaymentsSponsor"
          label="Global Payments Sponsor"
          required
          size={6}
          error={form.errors.globalPaymentsSponsor}
          value={form.values.globalPaymentsSponsor}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="globalPaymentsSponsorEmail"
          label="Sponsor Email"
          required
          size={6}
          error={form.errors.globalPaymentsSponsorEmail}
          value={form.values.globalPaymentsSponsorEmail}
          onChange={onFormValueChange}
        />

        {/* --------------------------------------- */}
        {/* Access Info */}
        {/* --------------------------------------- */}
        <Form.Title>Access Information</Form.Title>

        <Form.Radiogroup
          name="locationOfBusiness"
          label="Location of Business"
          required
          size={12}
          error={form.errors.locationOfBusiness}
          options={locationOptions}
          value={form.values.locationOfBusiness}
          onChange={(name, value) =>
            onFormValueChange("locationOfBusiness", value)
          }
        />

        <Form.Dropdown
          name="tenant"
          label="Tenant"
          required
          size={12}
          error={form.errors.tenant}
          options={tenantOptions}
          value={form.values.tenant}
          onChange={onFormValueChange}
        />

        <Form.Datebox
          name="accessStart"
          label="Access Start"
          size={6}
          value={form.values.accessStart}
          onChange={onFormValueChange}
        />

        <Form.Datebox
          name="accessEnd"
          label="Access End"
          size={6}
          value={form.values.accessEnd}
          onChange={onFormValueChange}
        />

        <Form.Textarea
          name="businessJustification"
          label="Business Justification"
          size={12}
          value={form.values.businessJustification}
          onChange={onFormValueChange}
        />
      </Form>
    </Wrapper>
  );
};
